import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { Plus, Trash2, Upload, Info } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { performanceService } from '../../services/performanceService';
import { formatCurrency, formatFullDate, toIsoDate } from '../../utils/format';

function PerformanceTab({ onStatus }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ date: '', value: '', contributedCapital: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsub = performanceService.subscribeToPerformance(
      (data) => {
        setPoints(data);
        setLoading(false);
      },
      (err) => {
        onStatus(`Error loading performance: ${err.message}`, true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [onStatus]);

  const addPoint = async () => {
    if (!draft.date) {
      onStatus('Please enter a date.', true);
      return;
    }
    try {
      await performanceService.upsertPoint(draft);
      setDraft({ date: '', value: '', contributedCapital: '' });
      onStatus('Performance point saved.');
    } catch (err) {
      onStatus(`Error saving: ${err.message}`, true);
    }
  };

  const deletePoint = async (id) => {
    try {
      await performanceService.deletePoint(id);
      onStatus('Point removed.');
    } catch (err) {
      onStatus(`Error deleting: ${err.message}`, true);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data
            .filter((r) => r.date && (r.Ragnar || r.value))
            .map((r) => ({
              date: r.date,
              value: r.Ragnar ?? r.value,
              contributedCapital: r['Contributed Capital'] ?? r.contributedCapital,
            }));
          if (rows.length === 0) {
            onStatus('No valid rows found in CSV.', true);
            return;
          }
          const count = await performanceService.importPoints(rows);
          onStatus(`Imported ${count} performance points.`);
        } catch (err) {
          onStatus(`Import error: ${err.message}`, true);
        } finally {
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      },
      error: (err) => onStatus(`CSV parse error: ${err.message}`, true),
    });
  };

  const seedFromStaticCsv = async () => {
    try {
      onStatus('Loading bundled CSV...');
      const res = await fetch('/monthly_portval9.csv');
      if (!res.ok) throw new Error(`Could not fetch CSV (${res.status})`);
      const text = await res.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const rows = parsed.data
        .filter((r) => r.date && r.Ragnar)
        .map((r) => ({
          date: r.date,
          value: r.Ragnar,
          contributedCapital: r['Contributed Capital'],
        }));
      const count = await performanceService.importPoints(rows);
      onStatus(`Seeded ${count} points from bundled CSV.`);
    } catch (err) {
      onStatus(`Seed error: ${err.message}`, true);
    }
  };

  if (loading) return <LoadingSpinner text="Loading performance..." />;

  return (
    <div>
      <div className="a-toolbar">
        <h2 className="a-section-title">Monthly history</h2>
        <div className="a-toolbar-actions">
          {points.length === 0 && (
            <button className="a-btn a-btn-ghost" onClick={seedFromStaticCsv}>
              <Upload size={16} /> Seed from bundled CSV
            </button>
          )}
          <button className="a-btn a-btn-ghost" onClick={() => fileInputRef.current?.click()}>
            <Upload size={16} /> Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      <div className="a-card a-card-pad" style={{ marginBottom: 18 }}>
        <div className="a-form-grid">
          <div className="a-field">
            <label>Date</label>
            <input
              className="a-input"
              type="date"
              value={toIsoDate(draft.date)}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </div>
          <div className="a-field">
            <label>Portfolio value ($)</label>
            <input
              className="a-input"
              type="number"
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              placeholder="e.g., 609721"
            />
          </div>
          <div className="a-field">
            <label>Contributed capital ($)</label>
            <input
              className="a-input"
              type="number"
              value={draft.contributedCapital}
              onChange={(e) => setDraft({ ...draft, contributedCapital: e.target.value })}
              placeholder="e.g., 332000"
            />
          </div>
          <button className="a-btn a-btn-primary" onClick={addPoint}>
            <Plus size={16} /> Add / update
          </button>
        </div>
      </div>

      <div className="a-table-wrap">
        <table className="a-table">
          <thead>
            <tr>
              <th>Date</th>
              <th className="num">Portfolio value</th>
              <th className="num">Contributed capital</th>
              <th className="num">Gain</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {points.map((p) => {
              const gain = (Number(p.value) || 0) - (Number(p.contributedCapital) || 0);
              return (
                <tr key={p.id}>
                  <td>{formatFullDate(p.date)}</td>
                  <td className="num">{formatCurrency(p.value)}</td>
                  <td className="num">{formatCurrency(p.contributedCapital)}</td>
                  <td className={`num ${gain < 0 ? 'a-neg' : 'a-pos'}`}>{formatCurrency(gain)}</td>
                  <td className="num">
                    <button
                      className="a-icon-btn danger"
                      onClick={() => deletePoint(p.id)}
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {points.length === 0 && (
              <tr>
                <td colSpan={5} className="a-empty-cell">
                  No performance data yet. Seed from the bundled CSV or add a point above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="a-hint">
        <Info size={14} /> Each month is keyed by its date, so importing or re-adding the same month
        updates it in place rather than creating duplicates.
      </p>
    </div>
  );
}

export default PerformanceTab;
